import React, { useEffect, useState, useRef } from "react";
import { Save, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { toast } from "sonner";

const DAYS = [
  ["monday", "Monday"], ["tuesday", "Tuesday"], ["wednesday", "Wednesday"],
  ["thursday", "Thursday"], ["friday", "Friday"], ["saturday", "Saturday"], ["sunday", "Sunday"],
];

export default function SettingsAdmin() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const heroRef = useRef();
  const bgRef = useRef();

  useEffect(() => { if (settings) setForm(settings); }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      const { id, languages, ...payload } = form;
      await api.put("/settings", payload);
      toast.success("Settings saved");
      refresh();
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setH = (day) => (e) => setForm({ ...form, hours_by_day: { ...(form.hours_by_day || {}), [day]: e.target.value } });

  const uploadImage = async (file, field) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const backend = process.env.REACT_APP_BACKEND_URL;
      setForm({ ...form, [field]: `${backend}${res.data.url}` });
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
  };

  return (
    <div data-testid="settings-admin">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif-display text-4xl text-gold-gradient">Home Page Edit</h1>
          <p className="text-sm text-[#D1C7C9]">Edit home page content, theme, hours, contacts and social links — everything customers see</p>
        </div>
        <button onClick={save} disabled={saving} className="btn-saffron disabled:opacity-50" data-testid="save-settings-btn">
          <Save size={14} /> {saving ? "Saving..." : "Save All"}
        </button>
      </div>

      <div className="space-y-6">
        <Section title="🏠 Home Page Content">
          <Field label="Restaurant Name" value={form.restaurant_name || ""} onChange={set("restaurant_name")} testId="set-name" />
          <Field label="Tagline (shown on hero in quotes)" value={form.tagline || ""} onChange={set("tagline")} testId="set-tagline" />
          <Field label="Hero Subtitle" value={form.hero_subtitle || ""} onChange={set("hero_subtitle")} testId="set-hero-sub" />
        </Section>

        <Section title="🪷 Promise Section (Soul of India)">
          <p className="text-xs text-[#D1C7C9]/60 -mt-2">Edit the section heading, intro text and the 3 promise cards shown on the home page.</p>
          <Field label="Eyebrow (small caps above title)" value={form.promise_eyebrow || ""} onChange={set("promise_eyebrow")} testId="set-promise-eyebrow" />
          <Field label="Section Title" value={form.promise_title || ""} onChange={set("promise_title")} testId="set-promise-title" />
          <FieldArea label="Intro paragraph" value={form.promise_text || ""} onChange={set("promise_text")} testId="set-promise" />

          {[1, 2, 3].map((n) => (
            <div key={n} className="border border-[#333] rounded-lg p-4 bg-[#0A0A0A]/40 space-y-3">
              <p className="text-xs uppercase tracking-widest text-[#C9A227]">Card {n}</p>
              <IconSelect
                label="Icon"
                value={form[`promise_card${n}_icon`] || ""}
                onChange={set(`promise_card${n}_icon`)}
                testId={`set-promise-card${n}-icon`}
              />
              <Field label="Title" value={form[`promise_card${n}_title`] || ""} onChange={set(`promise_card${n}_title`)} testId={`set-promise-card${n}-title`} />
              <Field label="Description" value={form[`promise_card${n}_desc`] || ""} onChange={set(`promise_card${n}_desc`)} testId={`set-promise-card${n}-desc`} />
            </div>
          ))}
        </Section>

        <Section title="🖼️ Background & Hero Images (optional)">
          <ImageField
            label="Background Image URL (decorative — overlays current pattern)"
            value={form.bg_image_url}
            onChange={set("bg_image_url")}
            onUpload={(f) => uploadImage(f, "bg_image_url")}
            inputRef={bgRef}
            testId="set-bg-image"
          />
          <ImageField
            label="Hero Image URL (right side fallback when 3D off)"
            value={form.hero_image_url}
            onChange={set("hero_image_url")}
            onUpload={(f) => uploadImage(f, "hero_image_url")}
            inputRef={heroRef}
            testId="set-hero-image"
          />
        </Section>

        <Section title="📞 Contact">
          <Field label="Phone" value={form.phone || ""} onChange={set("phone")} testId="set-phone" />
          <Field label="Email" value={form.email || ""} onChange={set("email")} testId="set-email" />
          <FieldArea label="Address" value={form.address || ""} onChange={set("address")} testId="set-address" />
          <FieldArea label="Google Maps Embed URL" value={form.google_maps_embed || ""} onChange={set("google_maps_embed")} testId="set-map" />
        </Section>

        <Section title="🕑 Opening Hours (per day)">
          <p className="text-xs text-[#D1C7C9]/60 -mt-2">Type "Closed" for the closed day. e.g. "11:30 AM – 2:30 PM, 5:30 – 9:00 PM"</p>
          {DAYS.map(([key, label]) => (
            <Field
              key={key}
              label={label}
              value={(form.hours_by_day || {})[key] || ""}
              onChange={setH(key)}
              testId={`set-hours-${key}`}
            />
          ))}
        </Section>

        <Section title="🔗 Social Links / Link Tree">
          <Field label="Instagram URL" value={form.instagram_url || ""} onChange={set("instagram_url")} testId="set-instagram" />
          <Field label="Facebook URL" value={form.facebook_url || ""} onChange={set("facebook_url")} testId="set-facebook" />
          <Field label="LINE URL" value={form.line_url || ""} onChange={set("line_url")} testId="set-line" />
          <Field label="Uber Eats URL" value={form.uber_url || ""} onChange={set("uber_url")} testId="set-uber" />
          <Field label="Google Review URL" value={form.google_review_url || ""} onChange={set("google_review_url")} testId="set-greview" />
        </Section>

        <Section title="🎨 Theme Colors">
          <div className="grid grid-cols-3 gap-3">
            <ColorField label="Primary (Saffron)" value={form.theme_primary || "#E69138"} onChange={set("theme_primary")} testId="set-primary" />
            <ColorField label="Accent (Gold)" value={form.theme_accent || "#D4AF37"} onChange={set("theme_accent")} testId="set-accent" />
            <ColorField label="Background (Maroon)" value={form.theme_background || "#1A0B0E"} onChange={set("theme_background")} testId="set-bg" />
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-6">
      <h3 className="font-serif-display text-2xl text-[#FDFBF7] mb-4">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Field({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-[#D1C7C9] block mb-1">{label}</span>
      <input value={value} onChange={onChange} data-testid={testId}
        className="w-full bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-sm text-[#FDFBF7] outline-none focus:border-[#E69138]" />
    </label>
  );
}
function FieldArea({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-[#D1C7C9] block mb-1">{label}</span>
      <textarea rows={3} value={value} onChange={onChange} data-testid={testId}
        className="w-full bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-sm text-[#FDFBF7] outline-none focus:border-[#E69138] resize-none" />
    </label>
  );
}
function ColorField({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-[#D1C7C9] block mb-1">{label}</span>
      <div className="flex items-center gap-2 bg-[#0A0A0A] border border-[#333] rounded-md px-2 py-1">
        <input type="color" value={value} onChange={onChange} data-testid={testId} className="w-10 h-8 rounded cursor-pointer bg-transparent" />
        <input value={value} onChange={onChange} className="flex-1 bg-transparent text-sm text-[#FDFBF7] outline-none" />
      </div>
    </label>
  );
}
function ImageField({ label, value, onChange, onUpload, inputRef, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#D1C7C9] block mb-1">{label}</label>
      {value && <img src={value} alt="" className="w-32 h-20 rounded-md object-cover mb-2 border border-[#333]" />}
      <div className="flex gap-2">
        <input value={value || ""} onChange={onChange} placeholder="Paste URL or upload"
          className="flex-1 bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-sm text-[#FDFBF7] outline-none focus:border-[#E69138]"
          data-testid={`${testId}-url`} />
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => onUpload(e.target.files[0])} />
        <button type="button" onClick={() => inputRef.current?.click()} className="btn-outline-gold text-xs px-3" data-testid={`${testId}-upload`}>
          <Upload size={12} /> Upload
        </button>
      </div>
    </div>
  );
}

const ICON_OPTIONS = [
  { key: "leaf", label: "🌿 Leaf" },
  { key: "heart", label: "♥ Heart" },
  { key: "flame", label: "🔥 Flame" },
  { key: "chef", label: "👨‍🍳 Chef Hat" },
  { key: "star", label: "★ Star" },
  { key: "utensils", label: "🍴 Utensils" },
  { key: "award", label: "🏆 Award" },
  { key: "sparkles", label: "✨ Sparkles" },
  { key: "coffee", label: "☕ Coffee" },
  { key: "salad", label: "🥗 Salad" },
];
function IconSelect({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-[#D1C7C9] block mb-1">{label}</span>
      <select value={value} onChange={onChange} data-testid={testId}
        className="w-full bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-sm text-[#FDFBF7] outline-none focus:border-[#E69138]">
        <option value="">— pick an icon —</option>
        {ICON_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
    </label>
  );
}

